import { freshUserStatus } from './utils';
import ProductButton from './ProductButton';
import { MODE } from './ProductButtonMode';

/**
 * Mediator class to manage communication between controls.
 */
export default class ProductButtonMediator {

	/**
	 * @param {object} options
	 */
	constructor( options = null ) {

		this.selector = options?.selector || '.colibri-product-button';
		this.labels = options?.labels

		// User information
		this.user = freshUserStatus();

		this.$c = {
			document: jQuery( 'document' ),
			body: jQuery( 'body' ),
			window: jQuery( 'window' )
		};

		this.controls = {};
		this.productStatuses = {};

		this.requested = {};

		this.isStateSelected = true;

		// Initialize controls
		this.initialize();
	}

	/**
	 * Initialize the 'Add to Cart' buttons and their event handlers.
	 * Optionally, initialize a new container if provided.
	 * @param {jQuery} $newContainer - Optional new container to initialize.
	 */
	initialize( $newContainer = null ) {
		// Get 'Add to Cart' containers (use new container if provided)
		const $containers = $newContainer || jQuery( this.selector );
		if ( !$containers.length ) return; // Exit if no containers found

		// Determine if a valid state is selected
		const $stateSelector = jQuery( '[data-module="state-selector"]' );
		if ( $stateSelector.length ) {
			const selectedState = $stateSelector.find( '.dropdown-toggle' ).text().trim() || $stateSelector.find( 'select' ).val() || 'none';
			this.isStateSelected = !['Select your State', 'none', ''].includes( selectedState );
		}

		// Initialize each 'Add to Cart' button container
		$containers.each( ( _, ctrl ) => {
			const $ctrl = jQuery( ctrl );
			const productCode = $ctrl.data( 'product-code' );
			if ( $ctrl.data( 'is-initialized' ) ) return; // Skip if already initialized

			$ctrl.data( 'is-initialized', true );
			this.register( productCode, new ProductButton( $ctrl, productCode, this, this.labels ) );
		} );

		// Perform status check for all product codes
		setTimeout( () => this.doStatusCheck( this.knownProductCodes() ), 1 );
	}

	/**
	 * Register a control with the mediator.
	 * @param {string} productCode - The product code.
	 * @param {ProductButton} control - The control instance.
	 */
	register( productCode, control ) {
		if ( !this.controls?.[ productCode ] ) {
			this.controls[ productCode ] = [];
		}
		this.controls[ productCode ].push( control );
		control.setMediator( this );

		// If we already have the product status, update the control
		if ( this.productStatuses[ productCode ] ) {
			control.receive( this.productStatuses[ productCode ] );
		}
	}

	/**
	 * Unregister a control from the mediator.
	 * @param {string} productCode - The product code.
	 * @param {ProductButton} control - The control instance.
	 */
	unregister( productCode, control ) {
		if ( this.controls?.[ productCode ] ) {
			this.controls[ productCode ] = this.controls[ productCode ].filter( col => col !== control );
		}
	}

	/**
	 * Notify controls about a product status update.
	 * @param {string} productCode - The product code.
	 * @param {Object} productStatus - The product status.
	 * @param {string} mode - The button mode (one of the possible states).
	 */
	notify( productCode, productStatus, mode = null ) {

		this.productStatuses[ productCode ] = productStatus;
		/**
		 * @var ProductButton[] controls
		 */
		const controls = this.controls[ productCode ];
		if ( controls ) {
			controls.forEach( ( control ) => {
				control.receive( productStatus, mode );
			} );
		}

		if ( mode ) {
			jQuery('body').trigger( 'ColibriEvent:' + mode, { productCode, productStatus } );
		}
	}

	/**
	 * Get all registered product codes.
	 * @returns {Array} - List of product codes.
	 */
	knownProductCodes() {
		return Object.keys( this.controls );
	}

	/**
	 * Perform a status check for a list of product codes.
	 * @param {Array} productCodes - The product codes to check.
	 */
	/**
	 * Perform a status check for a list of product codes.
	 * @param {Array} productCodes - The product codes to check.
	 */
	doStatusCheck( productCodes ) {
		if ( !Array.isArray( productCodes ) || productCodes.length === 0 ) return; // Do not check if product codes are unknown

		productCodes.forEach( productCode => this.notify( productCode, null, MODE.unchecked_progress ) );

		// Construct the base URL and query parameters
		const queryParams = new URLSearchParams( {
			subsidiaryId: window.colibri.subsidiaryId,
			...( this.user.studentId && { studentId: this.user.studentId } ),
			...( this.user.cartId && { shoppingCartID: this.user.cartId } )
		} );

		const url = `${window.colibri.studentApi}/productSearch/ProductProperties?${queryParams}`;

		jQuery.ajax( {
			async: true,
			crossDomain: true,
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			processData: false,
			url,
			data: JSON.stringify( { products: productCodes } )
		} ).done( response => this.handleStatusCheckResponse( response?.length ? response : [], productCodes ) )
		   .fail( () => this.handleStatusCheckResponse( null, productCodes ) );
	}

	/**
	 *
	 * @returns {boolean}
	 */
	isDebounceNeeded( what, productCode = '' ){
		if ( this.requested?.[what]?.[productCode] ) {
			return true;
		}

		this.requested[what] = this.requested[what] || {};

		this.requested[what][productCode] = true;
		return false;
	}

	/**
	 * Handle the response from the status check.
	 * @param {Array} response - The response from the status check.
	 * @param {Array} checkedProductCodes - The product codes that were checked.
	 */
	handleStatusCheckResponse( response, checkedProductCodes ) {

		if ( !response || !this.isStateSelected ) {
			// Disable all buttons if response is null or state is not selected
			this.knownProductCodes().forEach( productCode => this.notify( productCode, null, MODE.unavailable ) );
			return;
		}

		// Map to keep track of updated products
		const updatedProducts = new Set();

		// Process response and update product statuses
		response.forEach( ( { ProductCode: productCode, Properties: props = {}, EnrollmentId } ) => {
			if ( !checkedProductCodes.includes( productCode ) ) return;

			const productStatus = {
				inMembership: props.InMembership === 'True',
				isEnrolled: props.IsEnrolled === 'True',
				inCart: props.InCart === 'True',
				isInvalid: props.Invalid === 'True',
				enrollmentId: EnrollmentId || null,
				seatsRemaining: props.SeatsRemaining || false,
				upsell: props.UpsellMembershipName
					? {
						name: props.UpsellMembershipName,
						productCode: props.UpsellMembershipProductCode,
						monthlyPrice: props.UpsellMembershipMonthlyPrice,
						months: props.UpsellMembershipMonths,
					}
					: null,
			};

			updatedProducts.add( productCode ); // Track updated products

			this.notify( productCode, productStatus ); //mode will be determined based on productStatus
		} );

		// Notify mediator with default state for non-updated products
		checkedProductCodes
			.filter( productCode => !updatedProducts.has( productCode ) )
			.forEach( productCode => this.notify( productCode, null, MODE.sellable_prompt ) );

		jQuery('body').trigger( 'ColibriEvent:AFTER_STATUS_CHECK', {
			response,
			add_to_cart_requested: this.addToCartRequested
		} );
	}

}
